<template>
  <div class="sso-documentation">
    <!-- Header -->
    <div class="doc-header">
      <div class="container">
        <el-breadcrumb separator="/">
          <el-breadcrumb-item :to="{ path: '/' }">Home</el-breadcrumb-item>
          <el-breadcrumb-item>Dokumentasi</el-breadcrumb-item>
          <el-breadcrumb-item>SSO Integration Guide</el-breadcrumb-item>
        </el-breadcrumb>
        <h1 class="doc-title">
          <i class="el-icon-connection"></i>
          Panduan Integrasi SSO Intranet
        </h1>
        <p class="doc-subtitle">
          Dokumentasi lengkap untuk mengintegrasikan aplikasi Anda dengan Single Sign-On (SSO) Intranet LAN
        </p>
      </div>
    </div>

    <!-- Main Content -->
    <div class="doc-content container">
      <el-row :gutter="20">
        <!-- Sidebar Navigation -->
        <el-col :xs="24" :sm="24" :md="6" :lg="6">
          <div class="doc-sidebar" :class="{ 'sidebar-mobile': isMobile }">
            <div class="sidebar-header">
              <h3>Navigasi</h3>
              <el-button 
                v-if="isMobile" 
                type="text" 
                icon="el-icon-close" 
                @click="closeSidebar"
                class="close-btn"
              />
            </div>
            <el-menu
              :default-active="activeSection"
              class="doc-menu"
              @select="scrollToSection"
            >
              <el-menu-item index="overview">
                <i class="el-icon-document"></i>
                <span>Overview</span>
              </el-menu-item>
              <el-menu-item index="quick-start">
                <i class="el-icon-video-play"></i>
                <span>Quick Start</span>
              </el-menu-item>
              <el-menu-item index="registration">
                <i class="el-icon-user"></i>
                <span>Registrasi Client</span>
              </el-menu-item>
              <el-menu-item index="laravel">
                <i class="el-icon-document-copy"></i>
                <span>Integrasi Laravel</span>
              </el-menu-item>
              <el-menu-item index="spring-boot">
                <i class="el-icon-coffee-cup"></i>
                <span>Integrasi Spring Boot</span>
              </el-menu-item>
              <el-menu-item index="api-reference">
                <i class="el-icon-link"></i>
                <span>API Reference</span>
              </el-menu-item>
              <el-menu-item index="testing">
                <i class="el-icon-cpu"></i>
                <span>Testing</span>
              </el-menu-item>
              <el-menu-item index="troubleshooting">
                <i class="el-icon-warning"></i>
                <span>Troubleshooting</span>
              </el-menu-item>
              <el-menu-item index="faq">
                <i class="el-icon-question"></i>
                <span>FAQ</span>
              </el-menu-item>
              <el-menu-item index="support">
                <i class="el-icon-phone"></i>
                <span>Support</span>
              </el-menu-item>
            </el-menu>

            <!-- Download Buttons -->
            <div class="download-section">
              <h4>Download Dokumentasi</h4>
              <el-button 
                type="primary" 
                size="small" 
                icon="el-icon-download"
                @click="downloadPDF('full')"
                class="download-btn"
              >
                Full Guide (PDF)
              </el-button>
              <el-button 
                type="success" 
                size="small" 
                icon="el-icon-download"
                @click="downloadPDF('quick')"
                class="download-btn"
              >
                Quick Start (PDF)
              </el-button>
              <el-button 
                type="info" 
                size="small" 
                icon="el-icon-download"
                @click="downloadPDF('summary')"
                class="download-btn"
              >
                Summary (PDF)
              </el-button>
            </div>
          </div>

          <!-- Mobile Menu Button -->
          <el-button 
            v-if="isMobile" 
            type="primary" 
            icon="el-icon-menu"
            class="mobile-menu-btn"
            @click="openSidebar"
          >
            Menu Navigasi
          </el-button>
        </el-col>

        <!-- Main Documentation Content -->
        <el-col :xs="24" :sm="24" :md="18" :lg="18">
          <div class="doc-main">
            
            <!-- Overview Section -->
            <section id="overview" class="doc-section">
              <h2 class="section-title">
                <i class="el-icon-document"></i>
                Overview
              </h2>
              <el-alert
                title="Selamat Datang!"
                type="info"
                description="Dokumentasi ini akan memandu Anda untuk mengintegrasikan aplikasi dengan sistem Single Sign-On (SSO) Intranet LAN menggunakan protokol OAuth 2.0."
                :closable="false"
                show-icon
              />
              
              <div class="content-block">
                <h3>Apa itu SSO Intranet?</h3>
                <p>
                  SSO (Single Sign-On) Intranet LAN adalah sistem autentikasi terpusat yang memungkinkan pengguna 
                  untuk mengakses berbagai aplikasi dengan sekali login. Sistem ini menggunakan protokol 
                  <strong>OAuth 2.0 Authorization Code Flow</strong>.
                </p>

                <h3>Keuntungan Menggunakan SSO</h3>
                <el-row :gutter="20" class="feature-cards">
                  <el-col :xs="24" :sm="12" :md="8">
                    <el-card shadow="hover" class="feature-card">
                      <i class="el-icon-user feature-icon"></i>
                      <h4>Single Sign-On</h4>
                      <p>Login sekali untuk semua aplikasi</p>
                    </el-card>
                  </el-col>
                  <el-col :xs="24" :sm="12" :md="8">
                    <el-card shadow="hover" class="feature-card">
                      <i class="el-icon-lock feature-icon"></i>
                      <h4>Keamanan Terpusat</h4>
                      <p>Implementasi keamanan yang konsisten</p>
                    </el-card>
                  </el-col>
                  <el-col :xs="24" :sm="12" :md="8">
                    <el-card shadow="hover" class="feature-card">
                      <i class="el-icon-s-custom feature-icon"></i>
                      <h4>Manajemen User</h4>
                      <p>Manajemen user terpusat di Intranet</p>
                    </el-card>
                  </el-col>
                </el-row>

                <h3>Platform yang Didukung</h3>
                <el-tag type="success" class="platform-tag">
                  <i class="el-icon-document"></i> Laravel 8+
                </el-tag>
                <el-tag type="danger" class="platform-tag">
                  <i class="el-icon-coffee-cup"></i> Spring Boot 2.5+
                </el-tag>
                <el-tag type="info" class="platform-tag">
                  <i class="el-icon-star-on"></i> OAuth 2.0 Compatible
                </el-tag>
              </div>
            </section>

            <!-- Quick Start Section -->
            <section id="quick-start" class="doc-section">
              <h2 class="section-title">
                <i class="el-icon-video-play"></i>
                Quick Start
              </h2>
              
              <el-alert
                title="Setup Cepat dalam 15 Menit!"
                type="success"
                :closable="false"
                show-icon
              >
                <p>Ikuti langkah-langkah berikut untuk integrasi cepat.</p>
              </el-alert>

              <div class="content-block">
                <el-steps :active="quickStartStep" finish-status="success" align-center>
                  <el-step title="Registrasi Client" icon="el-icon-user"></el-step>
                  <el-step title="Setup Environment" icon="el-icon-setting"></el-step>
                  <el-step title="Implementasi Code" icon="el-icon-document"></el-step>
                  <el-step title="Testing" icon="el-icon-success"></el-step>
                </el-steps>

                <div class="step-content">
                  <h3>Langkah 1: Dapatkan Client Credentials</h3>
                  <p>Hubungi administrator SSO untuk mendaftarkan aplikasi Anda.</p>
                  <el-button type="primary" size="small" @click="scrollToSection('registration')">
                    Lihat Panduan Registrasi
                  </el-button>

                  <h3 style="margin-top: 30px;">Langkah 2: Environment URLs</h3>
                  <el-tabs v-model="envTab">
                    <el-tab-pane label="Development" name="dev">
                      <pre class="code-block">{{ devUrls }}</pre>
                    </el-tab-pane>
                    <el-tab-pane label="Production" name="prod">
                      <pre class="code-block">{{ prodUrls }}</pre>
                    </el-tab-pane>
                  </el-tabs>

                  <h3>Langkah 3: Pilih Platform</h3>
                  <el-row :gutter="20">
                    <el-col :xs="24" :sm="12">
                      <el-card shadow="hover" class="platform-card" @click.native="scrollToSection('laravel')">
                        <i class="el-icon-document platform-card-icon laravel-color"></i>
                        <h4>Laravel</h4>
                        <p>PHP Framework</p>
                        <el-button type="primary" size="small">Lihat Panduan</el-button>
                      </el-card>
                    </el-col>
                    <el-col :xs="24" :sm="12">
                      <el-card shadow="hover" class="platform-card" @click.native="scrollToSection('spring-boot')">
                        <i class="el-icon-coffee-cup platform-card-icon spring-color"></i>
                        <h4>Spring Boot</h4>
                        <p>Java Framework</p>
                        <el-button type="danger" size="small">Lihat Panduan</el-button>
                      </el-card>
                    </el-col>
                  </el-row>
                </div>
              </div>
            </section>

            <!-- Registration Section -->
            <section id="registration" class="doc-section">
              <h2 class="section-title">
                <i class="el-icon-user"></i>
                Registrasi Client SSO
              </h2>

              <div class="content-block">
                <p>Sebelum melakukan integrasi, Anda harus mendaftarkan aplikasi sebagai SSO client.</p>

                <h3>Informasi yang Dibutuhkan</h3>
                <el-table :data="registrationFields" stripe style="width: 100%">
                  <el-table-column prop="field" label="Field" width="180"></el-table-column>
                  <el-table-column prop="description" label="Deskripsi"></el-table-column>
                  <el-table-column prop="example" label="Contoh" width="200"></el-table-column>
                </el-table>

                <h3 style="margin-top: 30px;">Credentials yang Akan Diterima</h3>
                <el-alert
                  title="PENTING!"
                  type="warning"
                  description="Simpan client_secret dengan aman. Jangan commit ke repository publik!"
                  :closable="false"
                  show-icon
                />
                <pre class="code-block" style="margin-top: 15px;">{{ credentialsExample }}</pre>

                <h3>Hubungi Administrator</h3>
                <el-descriptions :column="1" border>
                  <el-descriptions-item label="Email">
                    <el-tag>admin-sso@lan.go.id</el-tag>
                  </el-descriptions-item>
                  <el-descriptions-item label="Phone">
                    <el-tag>(021) xxxx-xxxx</el-tag>
                  </el-descriptions-item>
                  <el-descriptions-item label="Estimasi">
                    <el-tag type="success">2-3 hari kerja</el-tag>
                  </el-descriptions-item>
                </el-descriptions>
              </div>
            </section>

            <!-- Laravel Integration Section -->
            <section id="laravel" class="doc-section">
              <h2 class="section-title">
                <i class="el-icon-document"></i>
                Integrasi Laravel
              </h2>

              <div class="content-block">
                <el-alert
                  title="Requirements"
                  type="info"
                  :closable="false"
                >
                  <ul>
                    <li>PHP 8.0+</li>
                    <li>Laravel 8+</li>
                    <li>Composer</li>
                    <li>Guzzle HTTP Client</li>
                  </ul>
                </el-alert>

                <h3 style="margin-top: 30px;">1. Install Dependencies</h3>
                <pre class="code-block">composer require guzzlehttp/guzzle</pre>

                <h3>2. Konfigurasi Environment</h3>
                <p>Edit file <code>.env</code>:</p>
                <pre class="code-block">{{ laravelEnvConfig }}</pre>

                <h3>3. Download Complete Code</h3>
                <p>Download file-file berikut untuk implementasi lengkap:</p>
                
                <el-alert
                  type="success"
                  :closable="false"
                  style="margin-bottom: 15px;"
                >
                  <div style="display: flex; justify-content: space-between; align-items: center;">
                    <span><i class="el-icon-info"></i> Download semua file sekaligus:</span>
                    <el-button 
                      type="success" 
                      size="small" 
                      icon="el-icon-download"
                      @click="downloadAllLaravel"
                    >
                      Download All Laravel Files
                    </el-button>
                  </div>
                </el-alert>

                <el-row :gutter="15">
                  <el-col :xs="24" :sm="12" :md="8">
                    <el-button 
                      type="primary" 
                      size="small" 
                      icon="el-icon-download"
                      @click="downloadCode('laravel', 'service')"
                      block
                    >
                      SSOService.php
                    </el-button>
                  </el-col>
                  <el-col :xs="24" :sm="12" :md="8">
                    <el-button 
                      type="primary" 
                      size="small" 
                      icon="el-icon-download"
                      @click="downloadCode('laravel', 'controller')"
                      block
                    >
                      SSOController.php
                    </el-button>
                  </el-col>
                  <el-col :xs="24" :sm="12" :md="8">
                    <el-button 
                      type="primary" 
                      size="small" 
                      icon="el-icon-download"
                      @click="downloadCode('laravel', 'routes')"
                      block
                    >
                      Routes Example
                    </el-button>
                  </el-col>
                </el-row>

                <h3 style="margin-top: 30px;">4. Add Routes</h3>
                <p>Tambahkan routes di <code>routes/web.php</code>:</p>
                <pre class="code-block">{{ laravelRoutes }}</pre>

                <h3>5. Add Login Button</h3>
                <p>Tambahkan button di halaman login:</p>
                <pre class="code-block">{{ laravelLoginButton }}</pre>

                <el-alert
                  title="✅ Selesai!"
                  type="success"
                  :closable="false"
                  style="margin-top: 20px;"
                >
                  <p>Implementasi Laravel sudah selesai. Lanjutkan ke testing!</p>
                </el-alert>
              </div>
            </section>

            <!-- Spring Boot Integration Section -->
            <section id="spring-boot" class="doc-section">
              <h2 class="section-title">
                <i class="el-icon-coffee-cup"></i>
                Integrasi Spring Boot
              </h2>

              <div class="content-block">
                <el-alert
                  title="Requirements"
                  type="info"
                  :closable="false"
                >
                  <ul>
                    <li>Java 11+</li>
                    <li>Spring Boot 2.5+</li>
                    <li>Maven or Gradle</li>
                  </ul>
                </el-alert>

                <h3 style="margin-top: 30px;">1. Add Dependencies</h3>
                <p>Tambahkan di <code>pom.xml</code>:</p>
                <pre class="code-block">{{ springDependencies }}</pre>

                <h3>2. Konfigurasi Application Properties</h3>
                <p>Edit <code>application.yml</code>:</p>
                <pre class="code-block">{{ springConfig }}</pre>

                <h3>3. Download Complete Code</h3>
                <p>Download file-file berikut untuk implementasi lengkap:</p>
                
                <el-alert
                  type="success"
                  :closable="false"
                  style="margin-bottom: 15px;"
                >
                  <div style="display: flex; justify-content: space-between; align-items: center;">
                    <span><i class="el-icon-info"></i> Download semua file sekaligus:</span>
                    <el-button 
                      type="success" 
                      size="small" 
                      icon="el-icon-download"
                      @click="downloadAllSpring"
                    >
                      Download All Spring Boot Files
                    </el-button>
                  </div>
                </el-alert>

                <el-row :gutter="15" style="margin-bottom: 15px;">
                  <el-col :xs="24" :sm="12" :md="6">
                    <el-button 
                      type="danger" 
                      size="small" 
                      icon="el-icon-download"
                      @click="downloadCode('spring', 'properties')"
                      block
                    >
                      SSOProperties.java
                    </el-button>
                  </el-col>
                  <el-col :xs="24" :sm="12" :md="6">
                    <el-button 
                      type="danger" 
                      size="small" 
                      icon="el-icon-download"
                      @click="downloadCode('spring', 'service')"
                      block
                    >
                      SSOService.java
                    </el-button>
                  </el-col>
                  <el-col :xs="24" :sm="12" :md="6">
                    <el-button 
                      type="danger" 
                      size="small" 
                      icon="el-icon-download"
                      @click="downloadCode('spring', 'controller')"
                      block
                    >
                      SSOController.java
                    </el-button>
                  </el-col>
                  <el-col :xs="24" :sm="12" :md="6">
                    <el-button 
                      type="danger" 
                      size="small" 
                      icon="el-icon-download"
                      @click="downloadCode('spring', 'config')"
                      block
                    >
                      RestTemplateConfig.java
                    </el-button>
                  </el-col>
                </el-row>

                <el-row :gutter="15">
                  <el-col :xs="24" :sm="12">
                    <el-button 
                      type="warning" 
                      size="small" 
                      icon="el-icon-document"
                      @click="downloadCode('spring', 'appconfig')"
                      block
                    >
                      application.yml
                    </el-button>
                  </el-col>
                  <el-col :xs="24" :sm="12">
                    <el-button 
                      type="info" 
                      size="small" 
                      icon="el-icon-document"
                      @click="downloadCode('spring', 'dependencies')"
                      block
                    >
                      dependencies.md
                    </el-button>
                  </el-col>
                </el-row>

                <el-alert
                  title="✅ Selesai!"
                  type="success"
                  :closable="false"
                  style="margin-top: 20px;"
                >
                  <p>Implementasi Spring Boot sudah selesai. Lanjutkan ke testing!</p>
                </el-alert>
              </div>
            </section>

            <!-- API Reference Section -->
            <section id="api-reference" class="doc-section">
              <h2 class="section-title">
                <i class="el-icon-link"></i>
                API Reference
              </h2>

              <div class="content-block">
                <h3>Endpoints</h3>
                <el-table :data="apiEndpoints" stripe style="width: 100%">
                  <el-table-column prop="method" label="Method" width="100">
                    <template slot-scope="scope">
                      <el-tag :type="getMethodType(scope.row.method)" size="small">
                        {{ scope.row.method }}
                      </el-tag>
                    </template>
                  </el-table-column>
                  <el-table-column prop="endpoint" label="Endpoint"></el-table-column>
                  <el-table-column prop="description" label="Deskripsi"></el-table-column>
                </el-table>

                <h3 style="margin-top: 30px;">OAuth 2.0 Flow</h3>
                <el-steps direction="vertical" :active="5">
                  <el-step title="User Click 'Login dengan Intranet'" icon="el-icon-user"></el-step>
                  <el-step title="Redirect ke SSO Authorization" icon="el-icon-right"></el-step>
                  <el-step title="User Login & Approve Consent" icon="el-icon-check"></el-step>
                  <el-step title="Callback dengan Authorization Code" icon="el-icon-back"></el-step>
                  <el-step title="Exchange Code untuk Access Token" icon="el-icon-key"></el-step>
                  <el-step title="Get User Info dengan Token" icon="el-icon-user-solid"></el-step>
                  <el-step title="Login Success!" icon="el-icon-success"></el-step>
                </el-steps>

                <h3 style="margin-top: 30px;">User Info Response</h3>
                <pre class="code-block">{{ userInfoResponse }}</pre>
              </div>
            </section>

            <!-- Testing Section -->
            <section id="testing" class="doc-section">
              <h2 class="section-title">
                <i class="el-icon-cpu"></i>
                Testing
              </h2>

              <div class="content-block">
                <h3>Manual Testing Checklist</h3>
                <el-checkbox-group v-model="testingChecklist">
                  <el-checkbox label="1" border>Click button "Login dengan Intranet"</el-checkbox>
                  <el-checkbox label="2" border>Redirect ke SSO dengan parameters benar</el-checkbox>
                  <el-checkbox label="3" border>Login dengan user Intranet berhasil</el-checkbox>
                  <el-checkbox label="4" border>Consent page muncul (jika first time)</el-checkbox>
                  <el-checkbox label="5" border>Redirect kembali dengan authorization code</el-checkbox>
                  <el-checkbox label="6" border>Token exchange berhasil</el-checkbox>
                  <el-checkbox label="7" border>User info berhasil diambil</el-checkbox>
                  <el-checkbox label="8" border>User berhasil login ke aplikasi</el-checkbox>
                  <el-checkbox label="9" border>Session tetap aktif saat refresh</el-checkbox>
                  <el-checkbox label="10" border>Logout berhasil dari aplikasi dan SSO</el-checkbox>
                </el-checkbox-group>

                <h3 style="margin-top: 30px;">Testing dengan cURL</h3>
                <el-collapse v-model="activeTestCollapse">
                  <el-collapse-item title="1. Token Exchange" name="1">
                    <pre class="code-block">{{ curlTokenExchange }}</pre>
                  </el-collapse-item>
                  <el-collapse-item title="2. Get User Info" name="2">
                    <pre class="code-block">{{ curlUserInfo }}</pre>
                  </el-collapse-item>
                  <el-collapse-item title="3. Logout" name="3">
                    <pre class="code-block">{{ curlLogout }}</pre>
                  </el-collapse-item>
                </el-collapse>
              </div>
            </section>

            <!-- Troubleshooting Section -->
            <section id="troubleshooting" class="doc-section">
              <h2 class="section-title">
                <i class="el-icon-warning"></i>
                Troubleshooting
              </h2>

              <div class="content-block">
                <h3>Common Issues</h3>
                <el-collapse v-model="activeTroubleshootCollapse">
                  <el-collapse-item 
                    v-for="(issue, index) in commonIssues" 
                    :key="index"
                    :title="issue.title" 
                    :name="String(index)"
                  >
                    <p><strong>Penyebab:</strong></p>
                    <ul>
                      <li v-for="(cause, idx) in issue.causes" :key="idx">{{ cause }}</li>
                    </ul>
                    <p><strong>Solusi:</strong></p>
                    <ul>
                      <li v-for="(solution, idx) in issue.solutions" :key="idx">{{ solution }}</li>
                    </ul>
                  </el-collapse-item>
                </el-collapse>

        
              </div>
            </section>

            <!-- FAQ Section -->
            <section id="faq" class="doc-section">
              <h2 class="section-title">
                <i class="el-icon-question"></i>
                Frequently Asked Questions
              </h2>

              <div class="content-block">
                <el-collapse v-model="activeFaqCollapse">
                  <el-collapse-item 
                    v-for="(faq, index) in faqs" 
                    :key="index"
                    :title="faq.question" 
                    :name="String(index)"
                  >
                    <p>{{ faq.answer }}</p>
                  </el-collapse-item>
                </el-collapse>
              </div>
            </section>

           
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- Footer -->
    <div class="doc-footer">
      <div class="container">
        <p>© 2026 Lembaga Administrasi Negara (LAN) - Pusat Data dan Informasi</p>
        <p>Dokumentasi SSO Intranet v1.0 - Terakhir diupdate: 20 Januari 2026</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SSODocumentationView',
  data() {
    return {
      activeSection: 'overview',
      isMobile: false,
      sidebarVisible: false,
      quickStartStep: 0,
      envTab: 'dev',
      testingChecklist: [],
      activeTestCollapse: [],
      activeTroubleshootCollapse: [],
      activeFaqCollapse: [],

      // URLs
      devUrls: `Frontend: https://dev-intranet.lan.go.id
API: https://apidev-intranet.lan.go.id

Authorize: https://dev-intranet.lan.go.id/sso/authorize
Token: https://apidev-intranet.lan.go.id/api/sso/token
UserInfo: https://apidev-intranet.lan.go.id/api/sso/userinfo
Logout: https://apidev-intranet.lan.go.id/api/sso/logout`,

      prodUrls: `Frontend: https://intranet.lan.go.id
API: https://api-intranet.lan.go.id

Authorize: https://intranet.lan.go.id/sso/authorize
Token: https://api-intranet.lan.go.id/api/sso/token
UserInfo: https://api-intranet.lan.go.id/api/sso/userinfo
Logout: https://api-intranet.lan.go.id/api/sso/logout`,

      // Registration Fields
      registrationFields: [
        { field: 'Nama Aplikasi', description: 'Nama aplikasi yang akan ditampilkan', example: 'Nama Apps' },
        { field: 'Deskripsi', description: 'Deskripsi singkat aplikasi', example: 'Sistem Informasi...' },
        { field: 'Redirect URI', description: 'URL callback aplikasi', example: 'https://app.lan.go.id/callback' },
        { field: 'Kontak PIC', description: 'Email atau kontak PIC teknis', example: 'developer@lan.go.id' }
      ],

      credentialsExample: `{
  "client_id": "client_e3024dd38e7843f39fabbcca929f382a",
  "client_secret": "83577378cf1e45cc85ce5ed95c28545e",
  "redirect_uris": [
    "https://yourapp.lan.go.id/sso/callback"
  ]
}`,

      // Laravel Config
      laravelEnvConfig: `SSO_CLIENT_ID=your_client_id_here
SSO_CLIENT_SECRET=your_client_secret_here
SSO_REDIRECT_URI=https://yourapp.lan.go.id/sso/callback

SSO_AUTHORIZE_URL=https://dev-intranet.lan.go.id/sso/authorize
SSO_TOKEN_URL=https://apidev-intranet.lan.go.id:8020/api/sso/token
SSO_USERINFO_URL=https://apidev-intranet.lan.go.id:8020/api/sso/userinfo
SSO_LOGOUT_URL=https://apidev-intranet.lan.go.id:8020/api/sso/logout

SSO_AUTO_CREATE_USER=true
SSO_DEFAULT_ROLE_ID=2`,

      laravelRoutes: `Route::prefix('sso')->group(function () {
    Route::get('/login', [SSOController::class, 'login'])->name('sso.login');
    Route::get('/callback', [SSOController::class, 'callback'])->name('sso.callback');
    Route::post('/logout', [SSOController::class, 'logout'])->name('sso.logout');
});`,

      laravelLoginButton: `<a href="{{ route('sso.login') }}" class="btn btn-success btn-block">
    <i class="fa fa-shield"></i> Login dengan Intranet
</a>`,

      // Spring Boot Config
      springDependencies: `<dependency>
    <groupId>org.springframework.boot</groupId>
    <artifactId>spring-boot-starter-web</artifactId>
</dependency>
<dependency>
    <groupId>org.springframework.boot</groupId>
    <artifactId>spring-boot-starter-security</artifactId>
</dependency>`,

      springConfig: `sso:
  client:
    id: your_client_id_here
    secret: your_client_secret_here
    redirect-uri: https://yourapp.lan.go.id/sso/callback
  endpoints:
    authorize: https://dev-intranet.lan.go.id/sso/authorize
    token: https://apidev-intranet.lan.go.id:8020/api/sso/token
    userinfo: https://apidev-intranet.lan.go.id:8020/api/sso/userinfo
    logout: https://apidev-intranet.lan.go.id:8020/api/sso/logout`,

      // API Endpoints
      apiEndpoints: [
        { method: 'GET', endpoint: '/sso/authorize', description: 'Mulai OAuth flow dan generate authorization code' },
        { method: 'POST', endpoint: '/api/sso/token', description: 'Exchange code untuk access token' },
        { method: 'GET', endpoint: '/api/sso/userinfo', description: 'Mendapatkan informasi user' },
        { method: 'POST', endpoint: '/api/sso/logout', description: 'Logout dari SSO' }
      ],

      userInfoResponse: `{
  "success": true,
  "data": {
    "username": "johndoe",
    "nip": "198501012010011001",
    "name": "John Doe",
    "email": "johndoe@lan.go.id",
    "jabatan": "Kepala Seksi Pengembangan",
    "unitKerja": "Bagian TI",
    "perEselonDua": "Pusat Data dan Informasi",
    "roles": ["ROLE_USER"]
  }
}`,

      // cURL Examples
      curlTokenExchange: `curl -X POST https://apidev-intranet.lan.go.id:8020/api/sso/token \\
  -H "Content-Type: application/x-www-form-urlencoded" \\
  -d "grant_type=authorization_code" \\
  -d "code=YOUR_AUTH_CODE" \\
  -d "client_id=YOUR_CLIENT_ID" \\
  -d "client_secret=YOUR_CLIENT_SECRET" \\
  -d "redirect_uri=YOUR_REDIRECT_URI"`,

      curlUserInfo: `curl -X GET https://apidev-intranet.lan.go.id:8020/api/sso/userinfo \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`,

      curlLogout: `curl -X POST https://apidev-intranet.lan.go.id:8020/api/sso/logout \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`,

      // Common Issues
      commonIssues: [
        {
          title: '❌ Invalid redirect_uri',
          causes: [
            'URL tidak exact match dengan yang didaftarkan',
            'Typo pada URL',
            'Protokol berbeda (http vs https)'
          ],
          solutions: [
            'Pastikan redirect_uri exact match',
            'Cek protokol (https di production)',
            'Cek trailing slash (/) di akhir',
            'Hubungi admin untuk update redirect_uri jika perlu'
          ]
        },
        {
          title: '❌ Invalid client_id or client_secret',
          causes: [
            'Client credentials salah',
            'Client belum diaktifkan',
            'Client sudah dihapus'
          ],
          solutions: [
            'Verifikasi client_id dan client_secret di .env',
            'Pastikan tidak ada whitespace atau karakter tersembunyi',
            'Hubungi admin untuk verifikasi status client',
            'Request regenerate client_secret jika perlu'
          ]
        },
        {
          title: '❌ Authorization code expired',
          causes: [
            'Code sudah digunakan (one-time use)',
            'Code expired (> 10 menit)',
            'Code tidak valid'
          ],
          solutions: [
            'Authorization code hanya valid 1x pakai dan 10 menit',
            'Jangan cache atau reuse authorization code',
            'Ulangi flow authorization dari awal'
          ]
        },
        {
          title: '❌ Token expired',
          causes: [
            'Access token sudah expired (default: 1 jam)',
            'Tidak ada refresh token'
          ],
          solutions: [
            'Implementasikan auto refresh token',
            'Simpan refresh_token dari response',
            'Refresh token sebelum access_token expired',
            'Handle 401 response dengan refresh atau re-login'
          ]
        }
      ],

      // FAQs
      faqs: [
        {
          question: 'Apakah SSO wajib menggunakan HTTPS?',
          answer: 'Ya, di production environment HTTPS wajib untuk keamanan. Di development boleh menggunakan HTTP dengan approval admin.'
        },
        {
          question: 'Berapa lama authorization code valid?',
          answer: 'Authorization code valid selama 10 menit dan hanya bisa digunakan sekali.'
        },
        {
          question: 'Berapa lama access token valid?',
          answer: 'Access token valid selama 1 jam (3600 detik). Gunakan refresh token untuk perpanjang session.'
        },
        {
          question: 'Apakah bisa multiple redirect_uri?',
          answer: 'Ya, saat registrasi bisa daftarkan multiple redirect_uri. Semua URI harus exact match saat digunakan.'
        },
        {
          question: 'Apakah user harus login ulang setiap kali akses aplikasi?',
          answer: 'Tidak. Jika user sudah login di SSO Intranet, mereka akan langsung di-redirect dengan code tanpa login ulang (SSO session masih aktif).'
        },
        {
          question: 'Bagaimana cara logout dari semua aplikasi?',
          answer: 'Logout dari SSO Intranet akan logout dari semua aplikasi yang terintegrasi (true SSO logout). Pastikan aplikasi Anda memanggil endpoint /api/sso/logout.'
        }
      ]
    }
  },

  mounted() {
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
    window.addEventListener('scroll', this.handleScroll);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile);
    window.removeEventListener('scroll', this.handleScroll);
  },

  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth < 768;
    },

    scrollToSection(section) {
      const element = document.getElementById(section);
      if (element) {
        const offset = 100;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });

        this.activeSection = section;
        if (this.isMobile) {
          this.closeSidebar();
        }
      }
    },

    handleScroll() {
      const sections = ['overview', 'quick-start', 'registration', 'laravel', 'spring-boot', 
                       'api-reference', 'testing', 'troubleshooting', 'faq', 'support'];
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            this.activeSection = section;
            break;
          }
        }
      }
    },

    openSidebar() {
      this.sidebarVisible = true;
      document.querySelector('.doc-sidebar').classList.add('sidebar-open');
    },

    closeSidebar() {
      this.sidebarVisible = false;
      document.querySelector('.doc-sidebar').classList.remove('sidebar-open');
    },

    getMethodType(method) {
      const types = {
        'GET': 'success',
        'POST': 'primary',
        'PUT': 'warning',
        'DELETE': 'danger'
      };
      return types[method] || 'info';
    },

    downloadPDF(type) {
      const files = {
        'full': {
          name: 'SSO_INTEGRATION_GUIDE.md',
          url: '/downloads/docs/SSO_INTEGRATION_GUIDE.md'
        },
        'quick': {
          name: 'SSO_QUICK_START.md',
          url: '/downloads/docs/SSO_QUICK_START.md'
        },
        'summary': {
          name: 'SSO_INTEGRATION_SUMMARY.md',
          url: '/downloads/docs/SSO_INTEGRATION_SUMMARY.md'
        }
      };
      
      const file = files[type];
      
      this.$message({
        message: `Downloading ${file.name}...`,
        type: 'info',
        duration: 2000
      });

      // Download file
      const link = document.createElement('a');
      link.href = file.url;
      link.download = file.name;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Show success message
      setTimeout(() => {
        this.$message({
          message: `${file.name} downloaded successfully!`,
          type: 'success',
          duration: 3000
        });
      }, 500);
    },

    downloadCode(platform, fileType) {
      const files = {
        laravel: {
          service: {
            name: 'SSOService.php',
            url: '/downloads/laravel/SSOService.php'
          },
          controller: {
            name: 'SSOController.php',
            url: '/downloads/laravel/SSOController.php'
          },
          routes: {
            name: 'routes-example.php',
            url: '/downloads/laravel/routes-example.php'
          }
        },
        spring: {
          properties: {
            name: 'SSOProperties.java',
            url: '/downloads/spring-boot/SSOProperties.java'
          },
          service: {
            name: 'SSOService.java',
            url: '/downloads/spring-boot/SSOService.java'
          },
          controller: {
            name: 'SSOController.java',
            url: '/downloads/spring-boot/SSOController.java'
          },
          config: {
            name: 'RestTemplateConfig.java',
            url: '/downloads/spring-boot/RestTemplateConfig.java'
          },
          appconfig: {
            name: 'application.yml',
            url: '/downloads/spring-boot/application.yml'
          },
          dependencies: {
            name: 'dependencies.md',
            url: '/downloads/spring-boot/dependencies.md'
          }
        }
      };

      const file = files[platform][fileType];
      
      if (!file) {
        this.$message.error('File not found!');
        return;
      }

      this.$message({
        message: `Downloading ${file.name}...`,
        type: 'info',
        duration: 2000
      });

      // Download file
      const link = document.createElement('a');
      link.href = file.url;
      link.download = file.name;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Show success message
      setTimeout(() => {
        this.$message({
          message: `${file.name} downloaded successfully!`,
          type: 'success',
          duration: 3000
        });
      }, 500);
    },

    downloadAllLaravel() {
      this.$message({
        message: 'Downloading all Laravel files...',
        type: 'info',
        duration: 2000
      });

      const files = ['service', 'controller', 'routes'];
      files.forEach((fileType, index) => {
        setTimeout(() => {
          this.downloadCode('laravel', fileType);
        }, index * 500);
      });
    },

    downloadAllSpring() {
      this.$message({
        message: 'Downloading all Spring Boot files...',
        type: 'info',
        duration: 2000
      });

      const files = ['properties', 'service', 'controller', 'config', 'appconfig', 'dependencies'];
      files.forEach((fileType, index) => {
        setTimeout(() => {
          this.downloadCode('spring', fileType);
        }, index * 500);
      });
    }
  }
}
</script>

<style scoped>
.sso-documentation {
  background: #f5f7fa;
  min-height: 100vh;
}

.doc-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 40px 20px;
  margin-bottom: 30px;
}

.doc-header .el-breadcrumb {
  margin-bottom: 20px;
}

.doc-header .el-breadcrumb >>> .el-breadcrumb__inner,
.doc-header .el-breadcrumb >>> .el-breadcrumb__separator {
  color: white !important;
}

.doc-header .el-breadcrumb >>> .el-breadcrumb__inner:hover {
  color: #e0e0e0 !important;
}

.doc-title {
  font-size: 36px;
  font-weight: bold;
  margin: 20px 0 10px;
}

.doc-subtitle {
  font-size: 16px;
  opacity: 0.9;
  margin: 0;
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Sidebar */
.doc-sidebar {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.1);
  position: sticky;
  top: 80px;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.sidebar-header h3 {
  margin: 0;
  font-size: 18px;
  color: #303133;
}

.doc-menu {
  border: none;
}

.doc-menu .el-menu-item {
  height: 45px;
  line-height: 45px;
  margin-bottom: 5px;
  border-radius: 4px;
}

.download-section {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
}

.download-section h4 {
  font-size: 14px;
  color: #606266;
  margin-bottom: 15px;
}

.download-btn {
  width: 100%;
  margin-bottom: 10px;
}

.mobile-menu-btn {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  box-shadow: 0 2px 12px rgba(0,0,0,0.3);
}

.sidebar-mobile {
  position: fixed;
  top: 0;
  left: -100%;
  height: 100vh;
  z-index: 2000;
  transition: left 0.3s;
  max-height: 100vh;
}

.sidebar-mobile.sidebar-open {
  left: 0;
}

/* Main Content */
.doc-main {
  background: white;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.1);
}

.doc-section {
  margin-bottom: 60px;
  scroll-margin-top: 100px;
}

.section-title {
  font-size: 28px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 3px solid #409eff;
}

.section-title i {
  margin-right: 10px;
  color: #409eff;
}

.content-block {
  margin-top: 20px;
}

.content-block h3 {
  font-size: 20px;
  color: #303133;
  margin: 25px 0 15px;
}

.content-block p {
  line-height: 1.8;
  color: #606266;
  margin-bottom: 15px;
}

.content-block ul {
  line-height: 1.8;
  color: #606266;
  padding-left: 25px;
}

.content-block ul li {
  margin-bottom: 8px;
}

.content-block code {
  background: #f5f7fa;
  padding: 2px 6px;
  border-radius: 3px;
  color: #e83e8c;
  font-family: 'Courier New', monospace;
}

/* Feature Cards */
.feature-cards {
  margin: 20px 0;
}

.feature-card {
  text-align: center;
  padding: 20px;
  cursor: default;
  transition: transform 0.3s;
}

.feature-card:hover {
  transform: translateY(-5px);
}

.feature-icon {
  font-size: 48px;
  color: #409eff;
  margin-bottom: 15px;
}

.feature-card h4 {
  font-size: 18px;
  margin: 10px 0;
  color: #303133;
}

.feature-card p {
  color: #909399;
  margin: 0;
}

.platform-tag {
  margin-right: 10px;
  margin-bottom: 10px;
  padding: 8px 15px;
  font-size: 14px;
}

.platform-tag i {
  margin-right: 5px;
}

/* Platform Cards */
.platform-card {
  text-align: center;
  padding: 30px 20px;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 15px;
}

.platform-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
}

.platform-card-icon {
  font-size: 64px;
  margin-bottom: 15px;
}

.laravel-color {
  color: #ff2d20;
}

.spring-color {
  color: #6db33f;
}

.platform-card h4 {
  font-size: 20px;
  margin: 10px 0;
  color: #303133;
}

.platform-card p {
  color: #909399;
  margin-bottom: 20px;
}

/* Code Blocks */
.code-block {
  background: #282c34;
  color: #abb2bf;
  padding: 20px;
  border-radius: 6px;
  overflow-x: auto;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.6;
  margin: 15px 0;
}

/* Steps */
.step-content {
  margin-top: 30px;
}

/* Support Cards */
.support-card {
  text-align: center;
  padding: 25px 15px;
  margin-bottom: 15px;
  cursor: default;
}

.support-icon {
  font-size: 48px;
  color: #409eff;
  margin-bottom: 15px;
}

.support-card h4 {
  font-size: 16px;
  margin: 10px 0;
  color: #303133;
}

.support-card p {
  font-size: 14px;
  color: #606266;
  margin: 10px 0;
}

/* Footer */
.doc-footer {
  background: #303133;
  color: white;
  text-align: center;
  padding: 30px 20px;
  margin-top: 60px;
}

.doc-footer p {
  margin: 5px 0;
  opacity: 0.8;
}

/* Responsive */
@media (max-width: 768px) {
  .doc-title {
    font-size: 24px;
  }

  .doc-subtitle {
    font-size: 14px;
  }

  .doc-main {
    padding: 20px;
  }

  .section-title {
    font-size: 22px;
  }

  .content-block h3 {
    font-size: 18px;
  }

  .code-block {
    font-size: 12px;
    padding: 15px;
  }
}
</style>
